import React from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { RouteComponentProps } from "react-router";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import routes from "../../constants/routes";
import { Typography, Card, Divider, Tabs, Tab } from "@material-ui/core";
import { CAM, CamStatus } from "../../constants/interfaces";
import {
  AttainmentRiskIcon,
  PageHeader,
  PageRoot,
  ResponseDueIcon,
  Button
} from "../../components";

import formatDate from "../../utils/formatDate";
import ResponseTab from "../../components/cam/ResponseTab";
import HistoryTab from "../../components/cam/HistoryTab";
import { camActions } from "../../reducers/cam";
import { camStateParse } from "../audit/CamList";
import { BreadcrumbLink } from "../../components/navigation/Breadcrumb";

const styles = (theme: Theme) =>
  createStyles({
    auditSection: {
      display: "flex",
      flexFlow: "row wrap",
      "& *": {
        marginRight: theme.spacing(2)
      }
    },
    camInfo: {
      marginRight: theme.spacing(3)
    },
    camInfoSection: {
      display: "flex",
      flexFlow: "row wrap",
      marginRight: theme.spacing(3)
    },
    card: {
      padding: theme.spacing(5)
    },
    corrrectActionTitle: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(4)
    },
    header: {
      display: "flex",
      flexFlow: "row wrap",
      margin: `${theme.spacing(4)}px 0`
    },
    headerItem: {
      marginBottom: theme.spacing(2),
      marginRight: theme.spacing(3)
    },
    headerLabel: {
      fontWeight: 700
    },
    tabMode: {
      marginBottom: theme.spacing(2)
    },
    back: {
      marginTop: "2em",
      float: "right"
    }
  });

export enum TabMode {
  RESPONSE = "response",
  HISTORY = "history"
}

type State = {
  tab: TabMode;
  view: boolean;
};

type Props = RouteComponentProps & {
  cam: CAM | null;
  classes?: any;
  getCam: (id: string) => Promise<void>;
};

class CamResponse extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const view = Boolean(_.get(this.props.location, "state.view", false));

    this.state = {
      tab: TabMode.RESPONSE,
      view
    };
    this.onTabChange = this.onTabChange.bind(this);
  }

  componentDidMount() {
    const {
      params: { id }
    }: any = this.props.match;

    this.props.getCam(id);
    if (this.state.view) {
      this.props.history.replace(
        `${this.props.location.pathname}${this.props.location.search}`,
        {}
      );
    }
  }

  onTabChange(e: any, value: TabMode) {
    this.setState({
      tab: value
    });
  }

  renderHeaderItem(label: string, content: any) {
    const classes = this.props.classes;
    return (
      <div className={classes.headerItem}>
        <Typography color="textSecondary" variant="caption">
          {label}
        </Typography>
        <Typography>{content}</Typography>
      </div>
    );
  }

  render() {
    const { cam, classes, history, location } = this.props;
    const { tab } = this.state;
    const viewOnly =
      _.get(cam, "camStatus") !== CamStatus.RESPONSE_REQUIRED ||
      this.state.view;

    const urlParams = new URLSearchParams(location.search);
    const from = urlParams.get("from");

    let breadcrumbLinks: BreadcrumbLink[] = [
      {
        label: routes.HOME.label,
        route: routes.HOME.url
      }
    ];
    let pageHeaderLabel = routes.CAM_RESPONSE.label;

    if (from === "CAM_LIST") {
      breadcrumbLinks = breadcrumbLinks.concat([
        {
          label: routes.CAM_LIST.label,
          onClick: () => history.goBack()
        }
      ]);
    } else if (from === "CAM_STATUS_REPORT") {
      pageHeaderLabel = "Corrective Action Summary";

      breadcrumbLinks = breadcrumbLinks.concat([
        {
          label: routes.AUDIT_STATUS_REPORT.label,
          route: routes.AUDIT_STATUS_REPORT.url
        },
        {
          label: routes.CAM_STATUS_REPORT.label,
          onClick: () => history.goBack()
        }
      ]);
    } else if (from === "CAM_AUDIT") {
      pageHeaderLabel = "Corrective Action Summary";

      breadcrumbLinks = breadcrumbLinks.concat([
        {
          label: routes.AUDIT_HISTORY.label,
          route: routes.AUDIT_HISTORY.url
        },
        {
          label: routes.CAM_STATUS_REPORT.label,
          onClick: () => history.goBack()
        }
      ]);
    }

    return (
      <PageRoot>
        <PageHeader label={pageHeaderLabel} breadcrumbLinks={breadcrumbLinks} />
        {cam && (
          <Card className={classes.card}>
            <header className={classes.header}>
              <div className={classes.camInfo}>
                <div className={classes.camInfoSection}>
                  {this.renderHeaderItem(
                    "Response Due",
                    <ResponseDueIcon dueDate={cam.responseDueDate} />
                  )}
                  {this.renderHeaderItem(
                    "Status",
                    camStateParse(cam.camStatus)
                  )}
                  {this.renderHeaderItem("Audit Type", cam.auditType)}
                  {this.renderHeaderItem("Audit Criterion", cam.criterion)}
                  {this.renderHeaderItem("Audit Criterion ID", cam.criterionId)}
                </div>
                <div className={classes.camInfoSection}>
                  {this.renderHeaderItem("Premises", cam.premisesname)}
                  {this.renderHeaderItem("Legal Entity", cam.legalEntityName)}
                  {this.renderHeaderItem(
                    "Attainment Risk",
                    <AttainmentRiskIcon risk={cam.attainmentRisk} />
                  )}
                  {this.renderHeaderItem(
                    "Audit Date",
                    formatDate.condensed(cam.auditDate)
                  )}
                </div>
                <div className={classes.camInfoSection}>
                  {this.renderHeaderItem("References", cam.reference)}
                  {this.renderHeaderItem("Guidance", cam.guidance)}
                </div>
                <div className={classes.camInfoSection}></div>
              </div>
            </header>
            <Divider />
            {cam.correctiveActionRequired && (
              <div>
                <article>
                  <Typography
                    className={classes.corrrectActionTitle}
                    variant="subtitle1"
                  >
                    Finding
                  </Typography>
                  <Typography color="textSecondary">{cam.finding}</Typography>
                </article>
                <article>
                  <Typography
                    className={classes.corrrectActionTitle}
                    variant="subtitle1"
                  >
                    Corrective Action Required
                  </Typography>
                  <Typography color="textSecondary">
                    {cam.correctiveActionRequired}
                  </Typography>
                </article>

                {viewOnly ? (
                  <div className={classes.back}>
                    <Button
                      id="cancel"
                      onClick={() => {
                        this.props.history.goBack();
                      }}
                    >
                      Back
                    </Button>
                  </div>
                ) : (
                  <article>
                    <Typography
                      className={classes.corrrectActionTitle}
                      variant="subtitle1"
                    >
                      Corrective Action Response
                    </Typography>
                    <Tabs
                      className={classes.tabMode}
                      onChange={this.onTabChange}
                      value={tab}
                    >
                      <Tab label="Response" value={TabMode.RESPONSE} />
                      <Tab label="History" value={TabMode.HISTORY} />
                    </Tabs>
                    {tab === TabMode.RESPONSE && (
                      <ResponseTab history={this.props.history} cam={cam} />
                    )}
                    {tab === TabMode.HISTORY && (
                      <HistoryTab history={cam.correctiveActionHistory} />
                    )}
                  </article>
                )}
              </div>
            )}
          </Card>
        )}
      </PageRoot>
    );
  }
}

export const styled = withStyles(styles)(CamResponse);

const mapDispatchToProps = {
  getCam: camActions.getCam
};

const mapStateToProps = ({ cam }: any, ownProps: Props) => {
  const {
    params: { id }
  }: any = ownProps.match;
  const currentCam = cam.cams.find((c: CAM) => c.caseId === id);
  return {
    cam: currentCam,
    error: cam.error,
    loading: cam.loading
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled);
